// frontend/src/components/ManagerTeamCompactCard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  People,
  TrendingUp,
  Visibility,
  Refresh,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '2px solid #fbcfe8',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(236, 72, 153, 0.15)',
    borderColor: '#f9a8d4',
  },
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: '#f43f5d8f',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
  }
}));

const ProgressBar = styled(Box)(({ theme, value }) => ({
  width: '100%',
  height: '6px',
  background: '#e0e0e0',
  borderRadius: '3px',
  overflow: 'hidden',
  marginTop: theme.spacing(1),
  '&::after': {
    content: '""',
    display: 'block',
    width: `${value}%`,
    height: '100%',
    background: value >= 75 ? '#4caf4f82' : value >= 50 ? '#ff99008a' : '#f4433679',
    borderRadius: '3px',
    transition: 'width 0.5s ease'
  }
}));

// Custom legend component
const CustomLegend = ({ data }) => {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
      {data.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color }} />
          <Typography variant="caption" sx={{ fontSize: '10px' }}>
            {entry.name}: {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

function ManagerTeamCompactCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  const getManagerIdFromToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.employee_id) {
        return payload.employee_id;
      }
      
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });
      
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching manager ID:", err);
      return null;
    }
  };

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  const fetchGlanceData = async () => {
    setLoading(true);
    try {
      const manager_id = await getManagerIdFromToken();
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!manager_id || !company_id) return;

      const response = await axios.get(
        `${API_URL}/api/attendance-requests/team-glance/${manager_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );

      if (response.data.success) {
        setData(response.data.data);
        setLastUpdated(new Date());
      }
      
      try {
        const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cmpRes.data && cmpRes.data.data) {
          setCompanyConfig(cmpRes.data.data);
        }
      } catch (e) {}
    } catch (error) {
      //console.error('Error fetching team glance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGlanceData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchGlanceData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchGlanceData();
  };

  const handleViewMore = () => {
    navigate('/manager/team-attendance');
  };

  // Prepare pie chart data
  const pieChartData = data ? [
    { name: 'Present', value: data.present || 0, color: '#4caf4f88' },
    { name: 'Late', value: data.late || 0, color: '#ff990099' },
    { name: 'Absent', value: data.absent || 0, color: '#f8695fc3' },
    { name: 'Leave', value: data.onLeave || 0, color: '#9b27b06d' }
  ] : [];

  if (loading && !data) {
    return (
      <StyledCard>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </CardContent>
      </StyledCard>
    );
  }

  if (!data) return null;

  return (
    <StyledCard>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
            <People sx={{ color: '#ec4899' }} />
            Team Today
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={`Last updated: ${lastUpdated?.toLocaleTimeString() || 'Just now'}`}>
              <Typography variant="caption" color="textSecondary">
                {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: companyConfig?.timezone || undefined })}
              </Typography>
            </Tooltip>
            <Tooltip title="Refresh">
              <Refresh fontSize="small" sx={{ cursor: 'pointer', color: '#9e9e9e' }} onClick={handleRefresh} />
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Small Pie Chart */}
        <Box sx={{ height: 200, position: 'relative', mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                innerRadius={25}
                outerRadius={50}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value, name) => [`${value} employees`, name]}
                contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Center total count */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {data.totalEmployees}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total
            </Typography>
          </Box>
        </Box>

        {/* Custom Legend */}
        <CustomLegend data={pieChartData} />

        {/* Attendance Rate */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="textSecondary">Attendance Rate</Typography>
            <Typography variant="body1" fontWeight="bold">{data.attendanceRate}%</Typography>
          </Box>
          <ProgressBar value={data.attendanceRate} />
        </Box>

        {/* Pending Approval Badge */}
        {data.pendingApprovals > 0 && (
          <Badge 
            badgeContent={data.pendingApprovals} 
            color="error" 
            sx={{ mb: 2, width: '100%' }}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box 
              sx={{ 
                p: 1, 
                bgcolor: '#fff3e0', 
                borderRadius: 2, 
                width: '100%',
                textAlign: 'center',
                cursor: 'pointer'
              }}
              onClick={handleViewMore}
            >
              <Typography variant="caption" fontWeight="bold" color="#ed6c02">
                {data.pendingLeaveApprovals > 0 && `${data.pendingLeaveApprovals} Leave `}
                {data.pendingCorrectionApprovals > 0 && `${data.pendingCorrectionApprovals} Correction `}
                Pending
              </Typography>
            </Box>
          </Badge>
        )}

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Visibility />}
          onClick={handleViewMore}
          sx={{ 
            borderRadius: '12px', 
            py: 1,
            borderColor: '#ec4899',
            color: '#ec4899',
            '&:hover': {
              borderColor: '#f43f5e',
              backgroundColor: 'rgba(236, 72, 153, 0.04)'
            }
          }}
        >
          View Full Report
        </Button>
      </CardContent>
    </StyledCard>
  );
}

export default ManagerTeamCompactCard;